/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Typography,
  Space,
  Button,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import {
  ShoppingCartOutlined,
  UserOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  ContainerOutlined,
  ImportOutlined,
  BarChartOutlined,
  RiseOutlined,
  FallOutlined,
  WalletOutlined,
  LineChartOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../../../shared/styles/Dashboard.css";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency, formatNumber } from "../../../utils/formatters";
import { fetchSystemConfig } from "../../../shared/lib/systemConfig";
import usePermissions from "../../../hooks/usePermissions";

const { Title, Text } = Typography;

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const [analyticsData, setAnalyticsData] = useState({
    total_customers: 0,
    total_products: 0,
    total_orders: 0,
    total_stock_weight_kg: 0,
    total_purchase_orders: 0,
    total_vendors: 0,
    financial_balances: null,
    currency_collected: null,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [categorySummary, setCategorySummary] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [, setSystemFxTick] = useState(0);

  const mainCurrency =
    analyticsData.financial_balances?.main_currency_code ||
    analyticsData.currency_collected?.main_currency_code ||
    "IQD";
  const secondaryCurrency = analyticsData.currency_collected?.secondary_currency_code || "";

  const netPositionMain = useMemo(() => {
    const fb = analyticsData.financial_balances;
    if (!fb) return 0;
    const ar = Number(fb.clients_debt_main ?? 0);
    const ap = Number(fb.our_debt_main ?? 0);
    return Math.round((ar - ap) * 100) / 100;
  }, [analyticsData.financial_balances]);

  /** Refresh cached system locale / currency so money columns match System Settings */
  useEffect(() => {
    let cancelled = false;
    fetchSystemConfig()
      .then(() => {
        if (!cancelled) setSystemFxTick((n) => n + 1);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const today = dayjs();
    setDateRange([today, today]);
    getAnalyticsData();
    getRecentOrders(today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD"));
  }, [i18n.language]);

  const getAnalyticsData = async () => {
    try {
      const { data } = await apiClient.get("/dashboard/summary");
      setAnalyticsData((prev) => ({
        ...prev,
        ...data,
        financial_balances: data.financial_balances ?? null,
        currency_collected: data.currency_collected ?? null,
      }));
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    }
  };

  const getRecentOrders = async (from, to, page = 1) => {
    setLoading(true);
    try {
      let url = `/dashboard/top-orders?limit=10&page=${page}`;
      if (from && to) {
        url += `&from=${from}&to=${to}`;
      }
      const response = await apiClient.get(url);
      setRecentOrders(
        (response.data.data || []).map((order) => ({
          id: order.id,
          customer: order.customer?.name || "-",
          date: order.created_at ? dayjs(order.created_at).format("DD/MM/YYYY") : "-",
          total: order.total_price,
        }))
      );
      setPagination({
        current: response.data.current_page,
        pageSize: Number(response.data.per_page),
        total: response.data.total,
      });
    } catch (error) {
      setRecentOrders([]);
    }
    setLoading(false);
  };

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      getRecentOrders(dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD"));
    } else {
      setDateRange([null, null]);
      setRecentOrders([]);
    }
  };

  const applyDatePreset = (key) => {
    const end = dayjs();
    let start = end;
    if (key === "today") start = end.clone().startOf("day");
    if (key === "7d") start = end.subtract(7, "day");
    if (key === "30d") start = end.subtract(30, "day");
    if (key === "month") start = end.startOf("month");
    const pair = [start, end];
    setDateRange(pair);
    getRecentOrders(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
  };

  const handleTableChange = (pag) => {
    getRecentOrders(
      dateRange[0]?.format("YYYY-MM-DD"),
      dateRange[1]?.format("YYYY-MM-DD"),
      pag.current
    );
  };

  const orderColumns = [
    {
      title: t("order"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("customer"),
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: t("date"),
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: t("total"),
      dataIndex: "total",
      key: "total",
      render: (val) =>
        val === null || val === undefined || val === "" ? "-" : formatCurrency(val, mainCurrency),
      width: 120,
    },
    ...(hasPermission("orders.view")
      ? [
          {
            title: t("actions"),
            key: "actions",
            width: 120,
            render: (_, record) => (
              <Link to={`/TableOrder/InfoOrder/${record.id}`}>
                <Button type="primary" size="small">
                  {t("View")}
                </Button>
              </Link>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    apiClient
      .get("/dashboard/top-customers?limit=5")
      .then((res) => setTopCustomers(res.data))
      .catch(() => setTopCustomers([]));

    apiClient
      .get("/dashboard/top-products?limit=5")
      .then((res) => setTopProducts(res.data))
      .catch(() => setTopProducts([]));
  }, [i18n.language]);

  useEffect(() => {
    const fetchCategorySummary = async () => {
      try {
        const response = await apiClient.get("/dashboard/category-summary", {
          params: {
            start_date: dateRange[0]?.format("YYYY-MM-DD"),
            end_date: dateRange[1]?.format("YYYY-MM-DD"),
          },
        });

        const summaryData = response.data.map((category) => {
          let categoryName;
          try {
            categoryName = parseLocalizedName(
              category.category_name,
              i18n.language,
              t("Unknown category")
            );
          } catch (error) {
            console.error("Error parsing category name:", category.category_name, error);
            categoryName = category.category_name;
          }

          return {
            name: categoryName,
            total_quantity: category.total_quantity,
            total_weight: category.total_weight,
          };
        });

        const sortedSummaryData = summaryData.sort((a, b) => {
          if (a.name === "Uncategorized") return 1;
          if (b.name === "Uncategorized") return -1;
          return a.name.localeCompare(b.name);
        });

        setCategorySummary(sortedSummaryData);
      } catch (error) {
        console.error("Error fetching category summary:", error);
      }
    };

    if (dateRange[0] && dateRange[1]) {
      fetchCategorySummary();
    }
  }, [dateRange, i18n.language]);

  useEffect(() => {
    const fetchStockSummary = async () => {
      try {
        const response = await apiClient.get("/dashboard/stock-category-summary");
        const processedStockSummary = response.data.map((item) => {
          let categoryName;
          try {
            categoryName = parseLocalizedName(item.category_name, i18n.language, t("Unknown category"));
          } catch (error) {
            console.error("Error parsing category name:", item.category_name, error);
            categoryName = item.category_name;
          }

          return {
            ...item,
            category_name: categoryName,
            total_stock_quantity: item.total_stock_quantity,
            total_stock_weight: item.total_stock_weight,
          };
        });

        setStockSummary(processedStockSummary);
      } catch (error) {
        console.error("Error fetching stock summary:", error);
      }
    };

    fetchStockSummary();
  }, []);

  const quickLinkItems = [
    hasPermission("purchase_orders.index")
      ? {
          key: "po",
          to: "/purchase-orders",
          icon: <ContainerOutlined />,
          label: t("purchaseOrdersMenu"),
        }
      : null,
    hasPermission("vendors.index")
      ? {
          key: "vendors",
          to: "/vendors",
          icon: <ImportOutlined />,
          label: t("vendorsMenu"),
        }
      : null,
    hasPermission("reports.view")
      ? {
          key: "buy",
          to: "/reports/buying",
          icon: <LineChartOutlined />,
          label: t("reportBuyingMenu"),
        }
      : null,
    hasPermission("reports.view")
      ? {
          key: "sell",
          to: "/reports/selling",
          icon: <BarChartOutlined />,
          label: t("reportSellingMenu"),
        }
      : null,
    hasPermission("reports.payables_receivables") || hasPermission("reports.view")
      ? {
          key: "pr",
          to: "/reports/payables-receivables",
          icon: <SwapOutlined />,
          label: t("payablesReceivablesMenu"),
        }
      : null,
    hasPermission("stock.view")
      ? {
          key: "stock",
          to: "/Stock",
          icon: <DatabaseOutlined />,
          label: t("stock"),
        }
      : null,
  ].filter(Boolean);

  const cashTotals = analyticsData.currency_collected?.cash;

  const showPoStat = hasPermission("purchase_orders.index");
  const showVendorsStat = hasPermission("vendors.index");
  const statCount = 4 + (showPoStat ? 1 : 0) + (showVendorsStat ? 1 : 0);
  const statMd = statCount <= 4 ? 6 : 8;
  const statLg = statCount <= 4 ? 6 : 4;

  return (
    <div className="dashboard-page">
      <header className="dashboard-hero">
        <Title level={2} className="dashboard-hero-title">
          {t("dashboard")}
        </Title>
        <Text type="secondary" className="dashboard-hero-desc">
          {t("dashboardHeroSubtitle")}
        </Text>
      </header>

      {quickLinkItems.length > 0 ? (
        <Card size="small" className="dashboard-quick-links" variant="borderless">
          <Space wrap size={[10, 10]} className="dashboard-quick-links-inner">
            {quickLinkItems.map((item) => (
              <Tooltip key={item.key} title={item.label}>
                <Link to={item.to}>
                  <Button
                    icon={item.icon}
                    size="middle"
                    className="dashboard-quick-link-btn"
                    aria-label={item.label}
                  />
                </Link>
              </Tooltip>
            ))}
          </Space>
        </Card>
      ) : null}

      <Row gutter={[16, 16]} className="dashboard-stats-row">
        <Col xs={24} sm={12} md={statMd} lg={statLg}>
          <Card variant="borderless" className="stat-card stat-card--customers">
            <Statistic
              title={t("customers")}
              value={analyticsData.total_customers}
              prefix={<UserOutlined className="dashboard-stat-icon dashboard-stat-icon--customers" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={statMd} lg={statLg}>
          <Card variant="borderless" className="stat-card stat-card--products">
            <Statistic
              title={t("products")}
              value={analyticsData.total_products}
              prefix={<AppstoreOutlined className="dashboard-stat-icon dashboard-stat-icon--products" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={statMd} lg={statLg}>
          <Card variant="borderless" className="stat-card stat-card--orders">
            <Statistic
              title={t("orders")}
              value={analyticsData.total_orders}
              prefix={<ShoppingCartOutlined className="dashboard-stat-icon dashboard-stat-icon--orders" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={statMd} lg={statLg}>
          <Card variant="borderless" className="stat-card stat-card--storage">
            <Statistic
              title={t("total_stock_weight")}
              value={analyticsData.total_stock_weight_kg ?? 0}
              precision={2}
              suffix=" kg"
              prefix={<DatabaseOutlined className="dashboard-stat-icon dashboard-stat-icon--storage" />}
            />
          </Card>
        </Col>
        {showPoStat ? (
          <Col xs={24} sm={12} md={statMd} lg={statLg}>
            <Card variant="borderless" className="stat-card stat-card--po">
              <Statistic
                title={t("dashboardStatPurchaseOrders")}
                value={analyticsData.total_purchase_orders ?? 0}
                prefix={<ContainerOutlined className="dashboard-stat-icon dashboard-stat-icon--po" />}
              />
            </Card>
          </Col>
        ) : null}
        {showVendorsStat ? (
          <Col xs={24} sm={12} md={statMd} lg={statLg}>
            <Card variant="borderless" className="stat-card stat-card--vendors">
              <Statistic
                title={t("dashboardStatVendors")}
                value={analyticsData.total_vendors ?? 0}
                prefix={<ImportOutlined className="dashboard-stat-icon dashboard-stat-icon--vendors" />}
              />
            </Card>
          </Col>
        ) : null}
      </Row>

      {analyticsData.financial_balances ? (
        <Card className="dashboard-finance-card" variant="borderless" title={t("dashboardSectionFinance")}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div className="dashboard-finance-tile dashboard-finance-tile--ar">
                <Text type="secondary" className="dashboard-finance-label">
                  <RiseOutlined /> {t("payablesReceivablesReceivable")}
                </Text>
                <div className="dashboard-finance-value">
                  {formatCurrency(analyticsData.financial_balances.clients_debt_main ?? 0, mainCurrency)}
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="dashboard-finance-tile dashboard-finance-tile--ap">
                <Text type="secondary" className="dashboard-finance-label">
                  <FallOutlined /> {t("payablesReceivablesPayable")}
                </Text>
                <div className="dashboard-finance-value">
                  {formatCurrency(analyticsData.financial_balances.our_debt_main ?? 0, mainCurrency)}
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="dashboard-finance-tile dashboard-finance-tile--net">
                <Text type="secondary" className="dashboard-finance-label">
                  <WalletOutlined /> {t("payablesReceivablesNet")}
                </Text>
                <div className="dashboard-finance-value">{formatCurrency(netPositionMain, mainCurrency)}</div>
              </div>
            </Col>
          </Row>

          {cashTotals ? (
            <div className="dashboard-cash-strip">
              <Text strong className="dashboard-cash-strip-title">
                {t("dashboardSectionCashFlow")}
              </Text>
              <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12}>
                  <Text type="secondary">{t("currencyTotalsCashGrandMain", { code: mainCurrency })}</Text>
                  <div className="dashboard-cash-amount">{formatCurrency(cashTotals.total_main ?? 0, mainCurrency)}</div>
                </Col>
                {secondaryCurrency ? (
                  <Col xs={24} sm={12}>
                    <Text type="secondary">{t("currencyTotalsCashGrandSecondary", { code: secondaryCurrency })}</Text>
                    <div className="dashboard-cash-amount">
                      {formatCurrency(cashTotals.total_secondary ?? 0, secondaryCurrency)}
                    </div>
                  </Col>
                ) : null}
              </Row>
              <Text type="secondary" className="dashboard-cash-hint">
                {t("dashboardCashFlowHint")}
              </Text>
            </div>
          ) : null}
        </Card>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title={t("Top Customers")} variant="borderless" className="dashboard-panel-card">
            <Table
              columns={[
                { title: t("Customer"), dataIndex: "customer", key: "customer" },
                { title: t("Orders"), dataIndex: "orders", key: "orders" },
                {
                  title: t("Total"),
                  dataIndex: "total_price",
                  key: "total_price",
                  render: (v) =>
                    v === null || v === undefined || v === "" ? "-" : formatCurrency(v, mainCurrency),
                },
              ]}
              dataSource={topCustomers.map((c, i) => ({ key: i + 1, ...c }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("Top Selling Products")} variant="borderless" className="dashboard-panel-card">
            <Table
              columns={[
                {
                  title: t("Product"),
                  dataIndex: "product",
                  key: "product",
                  render: (val) => {
                    try {
                      if (val) {
                        return parseLocalizedName(val, i18n.language, t("No product available"));
                      }
                    } catch (e) {
                      return val || t("No product available");
                    }
                    return t("No product available");
                  },
                },
                { title: t("Qty"), dataIndex: "qty", key: "qty" },
                {
                  title: t("Total"),
                  dataIndex: "revenue",
                  key: "revenue",
                  render: (v) =>
                    v === null || v === undefined || v === "" ? "-" : formatCurrency(v, mainCurrency),
                },
              ]}
              dataSource={topProducts.map((p, i) => ({ key: i + 1, ...p }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" variant="borderless" className="dashboard-toolbar-card">
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} lg={14}>
            <Space wrap size={[8, 8]} className="dashboard-presets">
              <Button size="small" onClick={() => applyDatePreset("today")}>
                {t("dashboardPresetToday")}
              </Button>
              <Button size="small" onClick={() => applyDatePreset("7d")}>
                {t("dashboardPreset7d")}
              </Button>
              <Button size="small" onClick={() => applyDatePreset("30d")}>
                {t("dashboardPreset30d")}
              </Button>
              <Button size="small" onClick={() => applyDatePreset("month")}>
                {t("dashboardPresetMonth")}
              </Button>
            </Space>
          </Col>
          <Col xs={24} lg={10}>
            <div className="dashboard-filter-ltr">
              <DatePicker.RangePicker
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                style={{ width: "100%", maxWidth: 360 }}
                allowClear
                placeholder={[t("start Date"), t("end Date")]}
                inputReadOnly
              />
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        title={t("Recent Orders")}
        style={{ marginBottom: 24 }}
        variant="borderless"
        className="dashboard-panel-card dashboard-recent-orders"
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={orderColumns}
          dataSource={recentOrders}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: true }}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title={t("Category Summary")} variant="borderless" className="dashboard-panel-card">
            <Table
              columns={[
                { title: t("Category"), dataIndex: "name", key: "name" },
                {
                  title: t("Total Quantity Sold"),
                  dataIndex: "total_quantity",
                  key: "total_quantity",
                  render: (value) => formatNumber(value ?? 0),
                },
                {
                  title: t("Total Weight Sold (kg)"),
                  dataIndex: "total_weight",
                  key: "total_weight",
                  render: (value) => formatNumber(value ?? 0),
                },
              ]}
              dataSource={categorySummary.map((item, index) => ({
                key: index,
                ...item,
              }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("Stock Summary")} variant="borderless" className="dashboard-panel-card">
            <Table
              columns={[
                {
                  title: t("Category"),
                  dataIndex: "category_name",
                  key: "category_name",
                },
                {
                  title: t("Total Stock Quantity"),
                  dataIndex: "total_stock_quantity",
                  key: "total_stock_quantity",
                  render: (value) => formatNumber(value ?? 0),
                },
                {
                  title: t("Total Stock Weight (kg)"),
                  dataIndex: "total_stock_weight",
                  key: "total_stock_weight",
                  render: (value) => formatNumber(value ?? 0),
                },
              ]}
              dataSource={stockSummary.map((item, index) => ({
                key: index,
                ...item,
              }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
